import { WalletContextState } from '@solana/wallet-adapter-react';
import { PublicKey, TransactionInstruction, VersionedTransaction } from '@solana/web3.js';
import * as BufferLayout from '@solana/buffer-layout';
import { u64 } from '@solana/buffer-layout-utils';
import { blob } from '@solana/buffer-layout';
import { MessageSignatureModel, TransferParamsWithToken, IInstructionInputData, InstructionType, AuthTokenModel } from '../models';
import { getAuthTransaction } from '../services';
import bs58 from 'bs58';

export class SolanaUtils {
   // private static connection: Connection = new Connection(process.env.REACT_APP_RPC || '');
   private static getWalletAuthentication = async (wallet: WalletContextState | any, message: string): Promise<Uint8Array | null> => {
      const messageEncoded = new TextEncoder().encode(message);

      // Wallet-Adapter standard
      if (wallet.signMessage) {
         const sig = await wallet.signMessage(messageEncoded);
         return sig instanceof Uint8Array ? sig : new Uint8Array(sig);
      }

      // Solana Mobile Wallet Adapter / in-app wallets
      if (wallet?.adapter?._wallet?.signMessage) {
         const sig = await wallet.adapter._wallet.signMessage(messageEncoded);
         return sig instanceof Uint8Array ? sig : new Uint8Array(sig);
      }

      return null;
   };

   static signMessage = async (wallet: WalletContextState, message: string, token: AuthTokenModel, isLedger: boolean = false): Promise<MessageSignatureModel> => {
      if (!wallet.publicKey?.toString()) throw new Error('Wallet public key not found');

      const signature: MessageSignatureModel = {
         tokenId: token.id,
         signer: wallet.publicKey.toString(),
      };

      if (isLedger) {
         const transactionResponse = await getAuthTransaction(message, wallet.publicKey.toString());
         if (!transactionResponse.data) throw new Error('Failed to get transaction');

         const versionedTx = VersionedTransaction.deserialize(Buffer.from(transactionResponse.data as string, 'base64'));
         const signedTx = await wallet?.signTransaction?.(versionedTx);
         if (!signedTx) throw new Error('Failed to sign transaction');
         const serializedTx = Buffer.from(signedTx.serialize()).toString('base64');

         signature.serializedTransaction = serializedTx;
      } else {
         const signedMessage = await this.getWalletAuthentication(wallet, `${message}`);
         if (!signedMessage) throw new Error('Failed to sign message');

         signature.signature = bs58.encode(signedMessage);
      }
      return signature;
   };

   static transferFromWeb3(params: TransferParamsWithToken): TransactionInstruction {
      const getTransferWithTokenType = (tokenLength: number) => {
         return {
            index: 2,
            layout: BufferLayout.struct<
               IInstructionInputData & {
                  lamports: bigint;
                  token: Uint8Array;
               }
            >([BufferLayout.u32('instruction'), u64('lamports'), blob(tokenLength, 'token')]),
         };
      };
      function getAlloc(type: any, fields: any): number {
         let alloc = 0;
         type.layout.fields.forEach((item: any) => {
            if (item.span >= 0) {
               alloc += item.span;
            } else if (typeof item.alloc === 'function') {
               alloc += item.alloc(fields[item.property]);
            }
         });
         return alloc;
      }
      function encodeData<TInputData extends IInstructionInputData>(type: InstructionType<TInputData>, fields?: any): Buffer {
         const allocLength = type.layout.span >= 0 ? type.layout.span : getAlloc(type, fields);
         const data = Buffer.alloc(allocLength);
         const layoutFields = Object.assign({ instruction: type.index }, fields);
         type.layout.encode(layoutFields, data);
         return data;
      }

      let data;
      let keys;
      const type = getTransferWithTokenType(params.token.length);
      data = encodeData(type, { lamports: BigInt(params.lamports), token: params.token });
      keys = [
         { pubkey: params.fromPubkey, isSigner: true, isWritable: true },
         { pubkey: params.toPubkey, isSigner: false, isWritable: true },
      ];

      return new TransactionInstruction({
         keys,
         programId: new PublicKey('11111111111111111111111111111111'),
         data,
      });
   }

   static signTransaction = async (serializedTransaction: string, wallet: WalletContextState): Promise<string> => {
      const versionedTx = VersionedTransaction.deserialize(Buffer.from(serializedTransaction, 'base64'));
      const signedTx = await wallet?.signTransaction?.(versionedTx);
      if (!signedTx) throw new Error('Failed to sign transaction');
      const serializedTx = Buffer.from(signedTx.serialize()).toString('base64');
      return serializedTx;
   };
}
